import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, ChevronDown, LayoutGrid, LogOut } from "lucide-react";
import { useCities } from "@/context/CityContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export function Logo({ onClick, dark = false }) {
  return (
    <button data-testid="brand-logo" onClick={onClick} className="flex items-center gap-2 group">
      <div className="h-9 w-9 rounded-lg bg-primary grid grid-cols-2 gap-0.5 p-1.5">
        <span className="bg-white/90 rounded-[2px]" /><span className="bg-white/50 rounded-[2px]" />
        <span className="bg-white/50 rounded-[2px]" /><span className="bg-white/90 rounded-[2px]" />
      </div>
      <div className="text-left leading-none">
        <div className="font-heading font-extrabold text-lg tracking-tight">Cleartile<span className="text-primary">.in</span></div>
        <div className={`text-[9px] uppercase tracking-[0.15em] font-semibold ${dark ? "text-white/50" : "text-muted-foreground"}`}>Where Stock Finds Its Buyer</div>
      </div>
    </button>
  );
}

export default function Header({ search, setSearch, onSearch }) {
  const nav = useNavigate();
  const { cities } = useCities();
  const { user, logout } = useAuth();
  const [q, setQ] = useState(search || "");

  useEffect(() => setQ(search || ""), [search]);

  const goDash = () => {
    if (!user) return;
    if (user.role === "admin") nav("/admin");
    else nav("/seller");
  };

  return (
    <header className="sticky top-0 z-40 bg-background/90 backdrop-blur border-b">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-4">
        <Logo onClick={() => nav("/")} />

        <form
          className="flex-1 max-w-xl hidden sm:flex items-center"
          onSubmit={(e) => { e.preventDefault(); onSearch ? onSearch(q) : nav("/"); }}
        >
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="search-tiles-input"
              value={q}
              onChange={(e) => { setQ(e.target.value); setSearch && setSearch(e.target.value); }}
              placeholder="Search tiles, size, surface, stock..."
              className="pl-9 rounded-full bg-secondary border-transparent focus-visible:border-primary"
            />
          </div>
        </form>

        <div className="ml-auto flex items-center gap-2">
          <button
            data-testid="active-city-pill"
            onClick={() => nav("/select-city")}
            className="hidden md:flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full bg-secondary hover:bg-accent transition-colors"
          >
            <MapPin className="h-4 w-4 text-primary" />
            {cities.length ? cities.join(" · ") : "Select City"}
            <ChevronDown className="h-3.5 w-3.5" />
          </button>

          {user && (user.role === "admin" || user.role) ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="account-menu-trigger" variant="outline" className="rounded-full font-semibold capitalize gap-1.5">
                  <LayoutGrid className="h-4 w-4" /> {user.role}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem data-testid="go-dashboard" onClick={goDash}>Dashboard</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-btn" onClick={() => { logout(); nav("/"); }}>
                  <LogOut className="h-4 w-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="signin-dropdown-trigger" className="rounded-full font-semibold gap-1.5">
                  Sign In <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Sign in as</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {["manufacturer", "wholesaler", "retailer", "admin"].map((r) => (
                  <DropdownMenuItem key={r} data-testid={`signin-${r}`} className="capitalize font-medium"
                    onClick={() => nav(`/signin?role=${r}`)}>
                    {r}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
}
